import * as React from "react";
import { cn } from "../lib/utils";
import { IconButton } from "./button";
import { Menu } from "lucide-react";

export interface TopBarProps extends Omit<React.HTMLAttributes<HTMLElement>, "children"> {
  /** Logo slot. Defaults to a compact AsrtLogo (import is injected to avoid cycles). */
  logo?: React.ReactNode;
  /** Left region next to the logo (breadcrumbs, project switcher). */
  left?: React.ReactNode;
  /** Center region (often the global search / command trigger). */
  center?: React.ReactNode;
  /** Right region (theme toggle, notifications, account). */
  right?: React.ReactNode;
  /** Renders a hamburger that calls this (mobile drawer toggle). */
  onToggleNav?: () => void;
  navOpen?: boolean;
}

const TopBar = React.forwardRef<HTMLElement, TopBarProps>(
  ({ className, logo, left, center, right, onToggleNav, navOpen, ...props }, ref) => (
    <header
      ref={ref}
      className={cn(
        "sticky top-0 z-40 w-full border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70",
        className,
      )}
      {...props}
    >
      <div className="flex h-14 items-center gap-3 px-4 sm:px-6">
        {onToggleNav ? (
          <IconButton
            variant="ghost"
            aria-label={navOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={Boolean(navOpen)}
            aria-controls="asrt-sidebar-nav"
            onClick={onToggleNav}
            className="lg:hidden"
          >
            <Menu aria-hidden="true" />
          </IconButton>
        ) : null}
        {logo ? <div className="flex items-center text-foreground">{logo}</div> : null}
        <div className="hidden min-w-0 items-center gap-2 md:flex">{left}</div>
        <div className="flex min-w-0 flex-1 items-center justify-center px-2">{center}</div>
        <div className="flex items-center gap-1.5">{right}</div>
      </div>
    </header>
  ),
);
TopBar.displayName = "TopBar";

export { TopBar };
